/** @jsxImportSource ./simboluri-jsx */
import { FundatieResult, StepState, fmtDate } from "./tipuri";
import { useT } from "../i18n";
import { stepDrawingUrl } from "./apiStructura";
import { DrawingView } from "./DwgViewer";
import Info from "./Info";
import Marime from "./Marime";
import { DescriereaPasului } from "./TitluPas";

// Pasul 5 — Fundație estimată: shows the LLM decisions (file + layers), the
// computed foundation length, and a preview with the selected layers plus
// the computed outline (rendered by the generic DrawingView).

export default function StepFundatie({
  projectId,
  cloneId = null,
  state,
  running,
}: {
  projectId: string;
  // clona soluției vizualizate din care se citește desenul; null = proiectul
  cloneId?: string | null;
  state: StepState | null;
  running: boolean;
}) {
  const t = useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.pas.ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.fnd.ruleaza")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.pas.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="fundatie_estimata" />
      </div>
    );
  }

  const r = state.result as unknown as FundatieResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.pas.esuat", {
            eroare: r?.error ?? t("struct.pas.eroare_necunoscuta"),
          })}
        </div>
        <div className="sg-muted">{t("struct.pas.ruleaza_din_nou")}</div>
      </div>
    );
  }

  return (
    <div className="st-result">
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.fnd.lungime")}
            <Marime cheie="L" />
          </div>
          <div className="st-card-value">{r.lungime_fundatie_m} m</div>
          <div className="st-card-sub">
            {r.nr_axe ? t("struct.fnd.nr_axe", { n: r.nr_axe }) : ""}
            {r.sursa_ax ?? t("struct.fnd.sursa_implicita")}
            {t("struct.fnd.intra_p6")}
          </div>
          {r.indreptare && (
            <div className="st-card-sub">
              {t("struct.fnd.axe_grila", {
                axe: r.indreptare.nr_axe, peGrila: r.indreptare.pe_grila,
              })}
              {r.indreptare.rotire_max_grade
                ? t("struct.fnd.rotire", { n: r.indreptare.rotire_max_grade })
                : ""}
              {"; "}
              {t("struct.fnd.abatere")}
              <b>{r.indreptare.abatere_max_m} m</b>
              <Info
                titlu={t("struct.fnd.info_axe_titlu")}
                ce={r.indreptare.nota ?? ""}
                deCe={t("struct.fnd.info_axe_de_ce")}
                parametri={[
                  { simbol: "θ₀", nume: t("struct.fnd.p_theta"), sursa: t("struct.fnd.p_theta_sursa") },
                  { simbol: t("struct.fnd.p_abatere"), nume: t("struct.fnd.p_abatere_nume"), sursa: t("struct.fnd.p_abatere_sursa") },
                  { simbol: t("struct.fnd.p_toleranta"), nume: t("struct.fnd.p_toleranta_nume"), sursa: t("struct.fnd.p_toleranta_sursa", { n: r.indreptare.toleranta_offset_m }) },
                ]}
              />
            </div>
          )}
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.fnd.regim")}
            <Info
              titlu={t("struct.fnd.info_regim_titlu")}
              ce={t("struct.fnd.info_regim_ce")}
              deCe={t("struct.fnd.info_regim_de_ce")}
              parametri={[
                { simbol: t("struct.fnd.p_niveluri"), nume: t("struct.fnd.p_niveluri_nume"), sursa: t("struct.fnd.p_niveluri_sursa") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.regim_inaltime || "?"}</div>
          <div className="st-card-sub">
            {r.nr_niveluri != null
              ? t("struct.fnd.nr_niveluri", { n: r.nr_niveluri }) : ""}
            {r.motiv_niveluri}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.fnd.suprafata")}
            <Marime cheie="A_desf" />
          </div>
          <div className="st-card-value">{r.suprafata_desfasurata_m2} m²</div>
          <div className="st-card-sub">
            {t("struct.fnd.suprafata_sub", {
              x: r.bbox_m[0], y: r.bbox_m[1],
            })}
          </div>
        </div>
      </div>

      {r.niveluri && r.niveluri.length > 0 && (
        <div className="st-findings">
          <h3>{t("struct.fnd.per_nivel")}</h3>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.top.nivel")}</th>
                <th>{t("struct.fnd.col_fisier")}</th>
                <th>{t("struct.fnd.col_suprafata")}</th>
                <th>{t("struct.fnd.col_sursa")}</th>
              </tr>
            </thead>
            <tbody>
              {r.niveluri.map((n, i) => (
                <tr key={i}>
                  <td><strong>{n.nivel}</strong></td>
                  <td className="sg-mono">{n.fisier}</td>
                  <td className="sg-mono">
                    {n.suprafata_m2 != null
                      ? `${n.suprafata_m2} m²`
                      : t("struct.fnd.eroare_nivel", { eroare: String(n.eroare) })}
                  </td>
                  <td className="sg-muted">{n.sursa ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="st-findings">
        <h3>{t("struct.fnd.identificat")}</h3>
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.fnd.fisier")}</span>
          <div>
            <strong>{r.fisier.name}</strong>
            <div className="sg-muted">{r.motiv_fisier}</div>
          </div>
        </div>
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.fnd.geometria")}</span>
          <div>
            <div className="sg-muted">{r.motiv_layere}</div>
          </div>
        </div>
        {/* Rulările de dinainte de pasul își alegea layerele de pe
            planșă și măsura banda brută de pereți. De atunci citește pereții și
            golurile MĂSURATE în Pasul 2, deci nu mai are layere de arătat. */}
        {(r.layere_pereti?.length ?? 0) > 0 && (
          <div className="st-finding">
            <span className="st-finding-label">{t("struct.fnd.layere_pereti")}</span>
            <div>
              {r.layere_pereti!.map((l) => <code key={l} className="st-chip">{l}</code>)}
            </div>
          </div>
        )}
        {(r.layere_goluri?.length ?? 0) > 0 && (
          <div className="st-finding">
            <span className="st-finding-label">{t("struct.fnd.layere_goluri")}</span>
            <div>
              {r.layere_goluri!.map((l) => <code key={l} className="st-chip">{l}</code>)}
              <div className="sg-muted">
                {t("struct.fnd.goluri_nota")}
              </div>
            </div>
          </div>
        )}
        <div className="sg-muted st-ran-at">
          {t("struct.pas.rulat_la", { cand: fmtDate(state.updated_at) })}
        </div>
      </div>

      <div className="st-preview">
        <DrawingView
          src={stepDrawingUrl(projectId, "fundatie_estimata", undefined, cloneId)}
          title={t("struct.fnd.desen")}
          embed
        />
      </div>
    </div>
  );
}
